<template>
    <div class="sticky-panel grid grid__custom-1" :class="rootClasses">
        <div class="sticky-panel--wrap" :style="wrapStyle">
            <div class="grid--container">
                <div class="grid--row">
                    <div class="sticky-panel--col grid--col" ref="content">
                        <div class="sticky-panel--main">
                            <slot :items="items" :scroll-to="scrollTo" :active-subtitle-index="activeSubtitleIndex"/>
                        </div>

                        <div class="sticky-panel--footer" v-if="$scopedSlots.footer">
                            <slot name="footer"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {
        CMS_ENTITIES_UNWRAPPED_SELECTOR,
        CMS_ENTITIES_WRAPPED_SELECTOR,
        IS_LOCKED_ATTRIBUTE,
        IS_ENABLED_ATTRIBUTE,
        MENU_ITEMS_NAME_ATTRIBUTE,
        MENU_ITEMS_SELECTOR,
    } from 'framework/global-components/sticky-panel/constants';
    import { bemMixin, isMountedMixin } from '@dreipol/vue-ui/src/mixins';
    import { debounce, last } from 'lodash';
    import { mapState } from 'vuex';
    import $ from 'bianco.query';
    import isMobileMixin from 'framework/mixins/is-mobile';
    import { get as getAttr, has as hasAttr } from 'bianco.attr';
    import { elementOffsetTop, scrollTop } from 'bianco.viewport';
    import { add as addEvent, remove as removeEvent } from 'bianco.events';

    /** @type IElementBoundaries */
    const BOUNDARIES_STRUCT = Object.freeze({
        top: 0,
        bottom: 0,
    });

    const DEBOUNCED_REFRESH_DELAY = 100;

    export default {
        mixins: [
            isMobileMixin,
            isMountedMixin,
            bemMixin('sticky-panel'),
        ],
        props: {},
        data() {
            return {
                // the last cms entity that might contain inline titles
                lastCmsUnwrappedEntityBoundaries: { ...BOUNDARIES_STRUCT },
                rootBoundaries: { ...BOUNDARIES_STRUCT },
                // all the page subtitles must be detected and mapped
                pageSubtitlesBoundaries: [],
                // all the wrapped cms entities should be mapped to hide the component if it will overlap them
                cmsWrappedEntitiesBoundaries: [],
                activeSubtitleIndex: 0,
                menuContentHeight: 0,
                cssTopPosition: 0,
                isHidden: false,
                isSticky: false,
            };
        },
        computed: {
            ...mapState('viewport', ['width']),
            rootClasses() {
                return [
                    this.bemIf(!this.isMobile && this.isSticky, 'is-sticky'),
                    this.bemIf(!this.isMobile && this.isHidden, 'is-hidden'),
                    this.bemFacets,
                ];
            },
            wrapStyle() {
                return this.isMobile ? null : { top: `${ this.cssTopPosition }px` };
            },
            items() {
                return this.pageSubtitles.map(el => {
                    const [path, label] = getAttr(el, ['id', MENU_ITEMS_NAME_ATTRIBUTE]);

                    return {
                        path: `#${ path }`,
                        isEnabled: hasAttr(el, IS_ENABLED_ATTRIBUTE),
                        isLockedContent: hasAttr(el, IS_LOCKED_ATTRIBUTE),
                        label,
                    };
                });
            },
            hasItems() {
                return Boolean(this.items.length);
            },
            pageSubtitles() {
                return this.isMounted ? $(MENU_ITEMS_SELECTOR) : [];
            },
            cmsUnwrappedEntities() {
                return this.isMounted ? $(CMS_ENTITIES_UNWRAPPED_SELECTOR) : [];
            },
            cmsWrappedEntities() {
                return this.isMounted ? $(CMS_ENTITIES_WRAPPED_SELECTOR) : [];
            },
            maxMenuTopOffset() {
                return this.lastCmsUnwrappedEntityBoundaries ? this.lastCmsUnwrappedEntityBoundaries.bottom - this.rootBoundaries.top - this.menuContentHeight : 0;
            },
        },
        methods: {
            /**
             * Get the boundaries of any DOM element. The boundaries are the top/bottom most edges in relation to the document height
             * @param {HTMLElement} el - target element
             * @return {IElementBoundaries}
             */
            getElementBoundaries(el) {
                const top = elementOffsetTop(el);

                return {
                    top,
                    bottom: top + el.offsetHeight,
                };
            },
            /**
             * Get the last CMS block boundaries. The Sticky menu should never scroll through its bottom edge
             * We will use this section to stick this component to the bottom
             * @return {IElementBoundaries} return the last section boundaries
             */
            getLastUnwrappedCmsEntityBoundaries() {
                if (this.cmsUnwrappedEntities.length) {
                    return this.getElementBoundaries(last(this.cmsUnwrappedEntities));
                }

                return this.rootBoundaries;
            },

            setBoundaries() {
                if (!this.$refs.content) {
                    return;
                }

                this.rootBoundaries = this.getElementBoundaries(this.$el);
                this.menuContentHeight = this.$refs.content.offsetHeight;
                this.lastCmsUnwrappedEntityBoundaries = this.getLastUnwrappedCmsEntityBoundaries();
                this.pageSubtitlesBoundaries = this.pageSubtitles.map(this.getElementBoundaries);
                this.cmsWrappedEntitiesBoundaries = this.cmsWrappedEntities.map(this.getElementBoundaries);
            },
            getMenuBottomPosition(currentScroll) {
                return currentScroll + this.menuContentHeight;
            },
            doesMenuOverlapWrappedEntities(currentScroll) {
                const menuBottomPosition = this.getMenuBottomPosition(currentScroll);

                return this.cmsWrappedEntitiesBoundaries.some(boundaries => {
                    const isCollidingBottom = menuBottomPosition > boundaries.top && currentScroll < boundaries.top;
                    const isCollidingTop = currentScroll > boundaries.top && currentScroll < boundaries.bottom;

                    return isCollidingTop || isCollidingBottom;
                });
            },
            getActiveSubtitleIndexByCurrentScroll(currentScrollTop) {
                return this.pageSubtitlesBoundaries.reduce((index, boundaries, currentIndex) => {
                    // remove -1 px in order to get properly the active state for the titles enabled via scrollIntoView
                    if (currentScrollTop > boundaries.top - 1) {
                        return currentIndex;
                    }

                    return index;
                }, -1); // start from -1 because we want to be consistent with the indexOf API
            },
            onScroll() {
                if (this.isMobile) {
                    return;
                }

                // getters
                const currentScrollTop = scrollTop();
                const menuBottomPosition = this.getMenuBottomPosition(currentScrollTop);
                const isAboveLastUnwrappedEntity = menuBottomPosition < this.lastCmsUnwrappedEntityBoundaries.bottom;
                const isBelowOriginalPosition = currentScrollTop > this.rootBoundaries.top;

                // setters
                this.isSticky = isBelowOriginalPosition && isAboveLastUnwrappedEntity;
                this.isHidden = isAboveLastUnwrappedEntity && this.doesMenuOverlapWrappedEntities(currentScrollTop);
                this.activeSubtitleIndex = this.getActiveSubtitleIndexByCurrentScroll(currentScrollTop);
                this.cssTopPosition = isAboveLastUnwrappedEntity ? 0 : this.maxMenuTopOffset;
            },
            setPageHashByActiveMenuItem(item) {
                history.replaceState(null, null, item.path);
            },
            scrollTo(event) {
                const [scrollTarget] = $(getAttr(event.currentTarget, 'href'));

                if (scrollTarget) {
                    scrollTarget.scrollIntoView({ behavior: 'smooth' });
                }
            },
            debouncedRefresh: debounce(function() {
                this.refresh();
            }, DEBOUNCED_REFRESH_DELAY),
            refresh() {
                this.setBoundaries();
                this.onScroll();
            },
        },
        watch: {
            width: {
                immediate: false,
                handler() {
                    // we need a nextTick here, because why not?!
                    // component should be refreshed once the DOM will be properly updated..ergo nextTick
                    this.$nextTick(this.refresh);
                },
            },
            activeSubtitleIndex(index) {
                if (this.items[index]) {
                    this.setPageHashByActiveMenuItem(this.items[index]);
                }
            },
        },
        beforeDestroy() {
            removeEvent(window, 'scroll', this.onScroll);

            // intercept all the possible user events on the DOM that might change the document height
            removeEvent(window, 'click keydown tap', this.debouncedRefresh);
        },
        mounted() {
            this.refresh();
            addEvent(window, 'scroll', this.onScroll, { passive: true });
            addEvent(window, 'click keydown tap', this.debouncedRefresh);
        },
    };
</script>
